import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import { useEffect } from 'react';
import { keyBy } from 'lodash';
import submitLead from '../../Helpers/submitLead';
import { getDynamicMatchSubmissionMetadata } from '../../Helpers/dynamicMatchSubmissionMetadata';
import { SUBMIT_LEAD_GQL } from '../../Helpers/submission';
import { getPreSubmissionComponents } from '../../Components/__selectors__/component.selectors';
import {
  getLegalStatementToShow,
  getStateAwareLegalStatement,
  selectCustomerStateCode,
} from '../../Components/LegalStatement/legalStatement.helpers';
import { updateSelectAndConnectBrandSubmissionStatus } from '../../Lib/redux/slices/submission';
import SubmissionStatus from '../../Data/SubmissionStatus';
import selectIsPolarisFeatureEnabled from '../../Helpers/polaris';

const useResubmissions = () => {
  const { useCidFromURL, iFrameId, widgetLabel, testMode } = useSelector(
    (store) => store.optionalFormConfig
  );
  const oneTrustLocale = useSelector((store) => store.oneTrustLocale);
  const { currentPage } = useSelector((store) => store.navigation);
  const { selectAndConnectBrands, submissionBlocks } = useSelector(
    (store) => store.submission
  );
  const isSubmissionBlocked = submissionBlocks.length > 0;
  const {
    countryCode,
    phoneNumberCountryCode,
    formId,
    campaignId,
    sourceSite,
    pageUrl,
    referrerUrl,
    isDomainInternal,
    localeCode,
    supplierId,
    privacyPolicy,
    navigationBarButtonTextConfig,
  } = useSelector((store) => store.formConfig);
  const { consentCertification } = useSelector((store) => store.trustedForm);
  const legalStatement = useSelector((store) => store.legalStatement);
  const { consentedSuppliersHash } = legalStatement;
  const customerStateCode = useSelector(selectCustomerStateCode);
  const { statement, consentedLegalStatementHash, supplierLegalStatements } =
    getStateAwareLegalStatement({ legalStatement, customerStateCode });
  const components = useSelector((store) => store.components);
  const { ancestorSid, id, shouldResubmit, firstSubmissionAttemptTimestamp } =
    useSelector((store) => store.submission);
  const { parentPageCookies } = useSelector((store) => store.cookies);
  const { eventPublicKeyString } = useSelector((store) => store.publicKeys);
  const { submissionPageNumber, subcategoryName, subcategoryId, featureFlags } =
    useSelector((store) => store.formConfig);
  const {
    customerMatches: customerBrandMatches,
    subcategoryMatches: subcategoryBrandMatches,
    dynamicMatches,
    matchLevel,
    supplierMatchSelectionEnabled,
  } = useSelector((store) => store.supplierMatches);
  const { visitorId } = useSelector((store) => store.userIdentities);
  const strategyCollectionsBySubscriptionId = useSelector(
    (store) => store.strategyCollections?.bySubscriptionId
  );
  const { themeName } = useSelector((store) => store.theme);
  const oneToOneSupplierConsent = useSelector((store) =>
    selectIsPolarisFeatureEnabled(store, 'oneToOneSupplierConsent')
  );
  const dynamicSelectAndConnect = useSelector(
    (store) => store.features?.dynamicSelectAndConnect
  );
  const {
    singleSupplierStatement,
    multipleSupplierStatement: multipleSupplierMatchStatement,
    singleSupplierConsentedLegalStatementHash,
    multipleSupplierConsentedLegalStatementHash,
  } = supplierLegalStatements;

  const [submitLeadMutation] = useMutation(SUBMIT_LEAD_GQL);
  const dispatch = useDispatch();
  const preSubmissionComponents = getPreSubmissionComponents(
    components,
    submissionPageNumber
  );

  const privacyPolicyUrl = privacyPolicy?.link || '';

  const consentStatement = getLegalStatementToShow({
    components,
    statement,
    supplierId,
    singleSupplierStatement,
    customerMatches: customerBrandMatches,
    multipleSupplierMatchStatement,
    currentPage,
    submitButtonText: navigationBarButtonTextConfig?.submit ?? '',
  });

  /**
   * If the state indicates that we should resubmit we should try to do so and if the submission is successful then submitLead will set shouldResubmit to false
   */
  useEffect(() => {
    if (shouldResubmit === true && !isSubmissionBlocked) {
      if (id !== undefined) {
        const { submissionType, dynamicBrandMatch } =
          getDynamicMatchSubmissionMetadata({
            dynamicMatches,
            brandId: dynamicMatches?.[0]?.brandId,
          });
        /**
         * Note the appointmentBooking is not included here as there is no resubmission for appointments
         */
        submitLead({
          ancestorSid,
          campaignId,
          components: preSubmissionComponents,
          consentedLegalStatementHash,
          consentedSuppliersHash,
          countryCode,
          localeCode,
          oneTrustLocale,
          pageUrl,
          parentPageCookies,
          phoneNumberCountryCode,
          referrerUrl,
          sourceSite,
          testMode,
          useCidFromURL,
          widgetLabel,
          dispatch,
          currentPage,
          iFrameId,
          submitLeadMutation,
          eventPublicKeyString,
          formId,
          isDomainInternal,
          submissionPageNumber,
          isResubmission: true,
          customerBrandMatches,
          subcategoryBrandMatches,
          multipleSupplierConsentedLegalStatementHash,
          singleSupplierConsentedLegalStatementHash,
          consentCertification,
          supplierId,
          visitorId,
          privacyPolicyUrl,
          consentStatement,
          subcategoryName,
          subcategoryId,
          firstSubmissionAttemptTimestamp,
          matchLevel,
          supplierMatchSelectionEnabled,
          themeName,
          featureFlags,
          submissionType,
          dynamicBrandMatch,
          dynamicSelectAndConnect,
          dynamicMatches,
          oneToOneSupplierConsent,
          strategyCollectionsBySubscriptionId,
        });
      }
    }
  }, [shouldResubmit]);

  useEffect(() => {
    if (
      !isSubmissionBlocked &&
      selectAndConnectBrands &&
      Object.keys(selectAndConnectBrands).length > 0
    ) {
      // if any select and connect brands are not started, set them to in progress and try to submit them
      Object.values(selectAndConnectBrands).forEach((brand) => {
        if (brand.submissionStatus === SubmissionStatus.NOT_STARTED) {
          dispatch(
            updateSelectAndConnectBrandSubmissionStatus({
              brandId: brand.brandId,
              submissionStatus: SubmissionStatus.IN_PROGRESS,
            })
          );

          const { submissionType, dynamicBrandMatch } =
            getDynamicMatchSubmissionMetadata({
              dynamicMatches,
              brandId: brand.brandId,
            });
          const selectAndConnectComponents = Object.values(
            preSubmissionComponents
          ).map((component) => {
            if (
              component.component.type === 'hidden' &&
              component.component.text === 'Brand (hidden)'
            ) {
              return {
                ...component,
                component: {
                  ...component.component,
                  defaultValue: brand.brandId,
                },
              };
            }
            return component;
          });

          submitLead({
            ancestorSid,
            campaignId,
            components: keyBy(
              selectAndConnectComponents,
              (component) => `component_${component.component.id}`
            ),
            consentedLegalStatementHash,
            consentedSuppliersHash,
            countryCode,
            localeCode,
            oneTrustLocale,
            pageUrl,
            parentPageCookies,
            phoneNumberCountryCode,
            referrerUrl,
            sourceSite,
            testMode,
            useCidFromURL,
            widgetLabel,
            dispatch,
            currentPage,
            iFrameId,
            submitLeadMutation,
            eventPublicKeyString,
            formId,
            isDomainInternal,
            submissionPageNumber,
            isResubmission: true,
            customerBrandMatches,
            subcategoryBrandMatches,
            multipleSupplierConsentedLegalStatementHash,
            singleSupplierConsentedLegalStatementHash,
            consentCertification,
            supplierId,
            visitorId,
            privacyPolicyUrl,
            consentStatement,
            subcategoryName,
            subcategoryId,
            firstSubmissionAttemptTimestamp,
            matchLevel,
            supplierMatchSelectionEnabled,
            selectAndConnectBrand: {
              brandId: brand.brandId,
              brandRevisionId: brand.brandRevisionId,
            },
            themeName,
            featureFlags,
            submissionType,
            dynamicBrandMatch,
            dynamicSelectAndConnect,
            dynamicMatches,
            oneToOneSupplierConsent,
          });
        }
      });
    }
  }, [selectAndConnectBrands]);

  return null;
};

export default useResubmissions;
